import React from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Calendar, 
  BarChart3, 
  LogOut, 
  User,
  Menu,
  X,
  Clock,
  CreditCard,
  Settings,
  DoorClosed,
  Gift,
  Package as PackageIcon,
  List,
  Tag,
  Newspaper,
  Sparkles,
  HelpCircle
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useState } from 'react';
import { NotificationBell } from './NotificationBell';
import logoImg from '../../assets/logo.png';

interface NavItem {
  path: string;
  label: string;
  icon: React.ReactNode;
}

const navItems: NavItem[] = [
  {
    path: '/backoffice/dashboard',
    label: 'Tableau de bord',
    icon: <BarChart3 className="w-5 h-5" />
  },
  {
    path: '/backoffice/bookings',
    label: 'Planning',
    icon: <Calendar className="w-5 h-5" />
  },
  {
    path: '/backoffice/bookings-list',
    label: 'Réservations',
    icon: <List className="w-5 h-5" />
  },
  {
    path: '/backoffice/gift-vouchers',
    label: 'Bons Cadeaux',
    icon: <Gift className="w-5 h-5" />
  },
  {
    path: '/backoffice/packages',
    label: 'Forfaits',
    icon: <PackageIcon className="w-5 h-5" />
  },
  {
    path: '/backoffice/availabilities',
    label: 'Disponibilités',
    icon: <Clock className="w-5 h-5" />
  },
  {
    path: '/backoffice/payments',
    label: 'Paiements',
    icon: <CreditCard className="w-5 h-5" />
  },
  {
    path: '/backoffice/room-closures',
    label: 'Fermetures salles',
    icon: <DoorClosed className="w-5 h-5" />
  },
  {
    path: '/backoffice/promo-codes',
    label: 'Codes promo',
    icon: <Tag className="w-5 h-5" />
  },
  {
    path: '/backoffice/blog',
    label: "Mag'Moman",
    icon: <Newspaper className="w-5 h-5" />
  },
  {
    path: '/backoffice/faq',
    label: 'FAQ',
    icon: <HelpCircle className="w-5 h-5" />
  },
  {
    path: '/backoffice/site-updates',
    label: 'Nouveautés',
    icon: <Sparkles className="w-5 h-5" />
  },
  {
    path: '/backoffice/system-settings',
    label: 'Paramètres',
    icon: <Settings className="w-5 h-5" />
  },
  {
    path: '/backoffice/services',
    label: 'Admin',
    icon: <Settings className="w-5 h-5" />
  },
];

export const BackofficeLayout: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/backoffice/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const isActivePath = (path: string) => {
    return location.pathname === path;
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar - Desktop */}
      <div className="hidden lg:flex lg:flex-shrink-0">
        <div className="flex flex-col w-64">
          <div className="flex flex-col flex-grow bg-white border-r border-gray-200 pt-5 pb-4 overflow-y-auto">
            {/* Logo */}
            <div className="flex items-center flex-shrink-0 px-4 mb-8">
              <img src={logoImg} alt="Logo Doux ce Moman" className="w-10 h-10 rounded-full bg-neutral-offwhite object-contain" />
              <span className="ml-3 text-xl font-semibold text-gray-900">
                Back Office
              </span>
            </div>

            {/* User Info */}
            <div className="px-4 mb-6 overflow-visible">
              <div className="flex items-center justify-between overflow-visible">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-gradient-to-r from-pink-500 to-purple-600 rounded-full flex items-center justify-center">
                    <User className="w-5 h-5 text-white" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-900">
                      {user?.practitioner.name}
                    </p>
                  </div>
                </div>
                <div className="overflow-visible">
                  <NotificationBell />
                </div>
              </div>
            </div>

            {/* Navigation */}
            <nav className="flex-1 px-2 space-y-1">
              {navItems.map((item) => (
                <button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  className={`
                    group flex items-center px-2 py-2 text-sm font-medium rounded-md w-full text-left
                    ${isActivePath(item.path)
                      ? 'bg-pink-50 text-pink-700 border-r-2 border-pink-500'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }
                  `}
                >
                  <span className={`
                    mr-3 flex-shrink-0
                    ${isActivePath(item.path) ? 'text-pink-500' : 'text-gray-400 group-hover:text-gray-500'}
                  `}>
                    {item.icon}
                  </span>
                  {item.label}
                </button>
              ))}
            </nav>

            {/* Logout Button */}
            <div className="px-2 mt-6">
              <button
                onClick={handleLogout}
                className="group flex items-center px-2 py-2 text-sm font-medium rounded-md text-gray-600 hover:bg-red-50 hover:text-red-700 w-full text-left"
              >
                <LogOut className="mr-3 flex-shrink-0 h-5 w-5 text-gray-400 group-hover:text-red-500" />
                Se déconnecter
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <div className={`lg:hidden ${isMobileMenuOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 flex z-40">
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setIsMobileMenuOpen(false)} />
          <div className="relative flex-1 flex flex-col max-w-xs w-full bg-white">
            <div className="absolute top-0 right-0 -mr-12 pt-2">
              <button
                className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <X className="h-6 w-6 text-white" />
              </button>
            </div>
            <div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
              {/* Mobile Logo */}
              <div className="flex items-center flex-shrink-0 px-4 mb-8">
                <img src={logoImg} alt="Logo Doux ce Moman" className="w-10 h-10 rounded-full bg-neutral-offwhite object-contain" />
                <span className="ml-3 text-xl font-semibold text-gray-900">
                  Back Office
                </span>
              </div>

              {/* Mobile User Info */}
              <div className="px-4 mb-6">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-gradient-to-r from-pink-500 to-purple-600 rounded-full flex items-center justify-center">
                    <User className="w-5 h-5 text-white" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-900">
                      {user?.practitioner.name}
                    </p>
                  </div>
                </div>
              </div>

              {/* Mobile Navigation */}
              <nav className="px-2 space-y-1">
                {navItems.map((item) => (
                  <button
                    key={item.path}
                    onClick={() => {
                      navigate(item.path);
                      setIsMobileMenuOpen(false);
                    }}
                    className={`
                      group flex items-center px-2 py-2 text-base font-medium rounded-md w-full text-left
                      ${isActivePath(item.path)
                        ? 'bg-pink-50 text-pink-700'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }
                    `}
                  >
                    <span className={`
                      mr-4 flex-shrink-0
                      ${isActivePath(item.path) ? 'text-pink-500' : 'text-gray-400 group-hover:text-gray-500'}
                    `}>
                      {item.icon}
                    </span>
                    {item.label}
                  </button>
                ))}
              </nav>
            </div>
            <div className="flex-shrink-0 flex border-t border-gray-200 p-4">
              <button
                onClick={handleLogout}
                className="group flex items-center px-2 py-2 text-base font-medium rounded-md text-gray-600 hover:bg-red-50 hover:text-red-700 w-full text-left"
              >
                <LogOut className="mr-4 flex-shrink-0 h-6 w-6 text-gray-400 group-hover:text-red-500" />
                Se déconnecter
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-col w-0 flex-1 overflow-hidden">
        {/* Mobile header */}
        <div className="lg:hidden">
          <div className="flex items-center justify-between bg-white border-b border-gray-200 px-4 py-2">
            <button
              onClick={() => setIsMobileMenuOpen(true)}
              className="text-gray-500 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-pink-500"
            >
              <Menu className="h-6 w-6" />
            </button>
            <div className="flex items-center">
              <img src={logoImg} alt="Logo Doux ce Moman" className="w-8 h-8 rounded-full bg-neutral-offwhite object-contain" />
              <span className="ml-2 text-lg font-semibold text-gray-900">
                Back Office
              </span>
            </div>
            <NotificationBell />
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1 relative overflow-y-auto focus:outline-none">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="py-8 px-6 lg:px-8"
          >
            <Outlet />
          </motion.div>
        </main>
      </div>
    </div>
  );
};
